var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { openAlert, closeAlerts } from './ui/alert/Alert';
import { closeToast, openToast } from './ui/toast/Toast';
import { Logger } from './utils/Logger';
import { shortenString } from './utils/shorten-string';
import { BeaconErrorType } from './types/BeaconErrorType';
import { UnknownBeaconError, BeaconError } from '.';
import { isMobile } from './utils/platform';
const logger = new Logger('BeaconEvents');
const SUCCESS_TIMER = 5 * 1000;
/**
 * The different events that can be emitted by the beacon-sdk
 */
export var BeaconEvent;
(function (BeaconEvent) {
    BeaconEvent["PERMISSION_REQUEST_SENT"] = "PERMISSION_REQUEST_SENT";
    BeaconEvent["PERMISSION_REQUEST_SUCCESS"] = "PERMISSION_REQUEST_SUCCESS";
    BeaconEvent["PERMISSION_REQUEST_ERROR"] = "PERMISSION_REQUEST_ERROR";
    BeaconEvent["OPERATION_REQUEST_SENT"] = "OPERATION_REQUEST_SENT";
    BeaconEvent["OPERATION_REQUEST_SUCCESS"] = "OPERATION_REQUEST_SUCCESS";
    BeaconEvent["OPERATION_REQUEST_ERROR"] = "OPERATION_REQUEST_ERROR";
    BeaconEvent["SIGN_REQUEST_SENT"] = "SIGN_REQUEST_SENT";
    BeaconEvent["SIGN_REQUEST_SUCCESS"] = "SIGN_REQUEST_SUCCESS";
    BeaconEvent["SIGN_REQUEST_ERROR"] = "SIGN_REQUEST_ERROR";
    // TODO: ENCRYPTION
    // ENCRYPT_REQUEST_SENT = 'ENCRYPT_REQUEST_SENT',
    // ENCRYPT_REQUEST_SUCCESS = 'ENCRYPT_REQUEST_SUCCESS',
    // ENCRYPT_REQUEST_ERROR = 'ENCRYPT_REQUEST_ERROR',
    BeaconEvent["BROADCAST_REQUEST_SENT"] = "BROADCAST_REQUEST_SENT";
    BeaconEvent["BROADCAST_REQUEST_SUCCESS"] = "BROADCAST_REQUEST_SUCCESS";
    BeaconEvent["BROADCAST_REQUEST_ERROR"] = "BROADCAST_REQUEST_ERROR";
    BeaconEvent["ACKNOWLEDGE_RECEIVED"] = "ACKNOWLEDGE_RECEIVED";
    BeaconEvent["LOCAL_RATE_LIMIT_REACHED"] = "LOCAL_RATE_LIMIT_REACHED";
    BeaconEvent["NO_PERMISSIONS"] = "NO_PERMISSIONS";
    BeaconEvent["ACTIVE_ACCOUNT_SET"] = "ACTIVE_ACCOUNT_SET";
    BeaconEvent["ACTIVE_TRANSPORT_SET"] = "ACTIVE_TRANSPORT_SET";
    BeaconEvent["SHOW_PREPARE"] = "SHOW_PREPARE";
    BeaconEvent["HIDE_UI"] = "HIDE_UI";
    BeaconEvent["PAIR_INIT"] = "PAIR_INIT";
    BeaconEvent["PAIR_SUCCESS"] = "PAIR_SUCCESS";
    BeaconEvent["CHANNEL_CLOSED"] = "CHANNEL_CLOSED";
    BeaconEvent["INTERNAL_ERROR"] = "INTERNAL_ERROR";
    BeaconEvent["UNKNOWN"] = "UNKNOWN";
})(BeaconEvent || (BeaconEvent = {}));
/**
 * Show a "Request sent" toast
 */
const showSentToast = (data) => __awaiter(void 0, void 0, void 0, function* () {
    let openWalletAction;
    const actions = [];
    if (data.walletInfo.deeplink) {
        if (data.walletInfo.type === 'web' ||
            (data.walletInfo.type === 'mobile' && isMobile(window)) ||
            (data.walletInfo.type === 'desktop' && !isMobile(window))) {
            const link = data.walletInfo.deeplink;
            openWalletAction = () => __awaiter(void 0, void 0, void 0, function* () {
                const a = document.createElement('a');
                a.setAttribute('href', link);
                a.setAttribute('target', '_blank');
                a.dispatchEvent(new MouseEvent('click', { view: window, bubbles: true, cancelable: true }));
            });
        }
    }
    actions.push({
        text: `No answer from your wallet received yet. Please make sure the wallet is open.`,
        isBold: true
    });
    actions.push({
        text: 'Did you make a mistake?',
        actionText: 'Cancel Request',
        actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
            yield closeToast();
        })
    });
    actions.push({
        text: 'Wallet not receiving request?',
        actionText: 'Reset Connection',
        actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
            yield closeToast();
            // eslint-disable-next-line @typescript-eslint/unbound-method
            const resetCallback = data.extraInfo.resetCallback;
            if (resetCallback) {
                logger.log('showSentToast', 'resetCallback invoked');
                yield resetCallback();
            }
        })
    });
    openToast({
        body: `Request sent to\u00A0 {{wallet}}`,
        walletInfo: data.walletInfo,
        state: 'loading',
        actions,
        openWalletAction
    }).catch((toastError) => console.error(toastError));
});
const showAcknowledgedToast = (data) => __awaiter(void 0, void 0, void 0, function* () {
    openToast({
        body: 'Awaiting confirmation in\u00A0 {{wallet}}',
        state: 'acknowledge',
        walletInfo: data.walletInfo
    }).catch((toastError) => console.error(toastError));
});
const showPrepare = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const text = data.walletInfo
        ? `Preparing Request for\u00A0 {{wallet}}...`
        : 'Preparing Request...';
    openToast({
        body: text,
        state: 'prepare',
        walletInfo: data.walletInfo
    }).catch((toastError) => console.error(toastError));
});
const hideUI = (elements) => __awaiter(void 0, void 0, void 0, function* () {
    if (elements) {
        if (elements.includes('alert')) {
            closeAlerts();
        }
        if (elements.includes('toast')) {
            closeToast();
        }
    }
    else {
        closeToast();
    }
});
/**
 * Show a "No Permission" alert
 */
const showNoPermissionAlert = () => __awaiter(void 0, void 0, void 0, function* () {
    yield openAlert({
        title: 'No Permission',
        body: 'Please allow the wallet to handle this type of request.'
    });
});
/**
 * Show an error toast
 *
 * @param beaconError The beacon error
 */
const showErrorToast = (response, buttons) => __awaiter(void 0, void 0, void 0, function* () {
    const error = response.errorResponse.errorType
        ? BeaconError.getError(response.errorResponse.errorType, response.errorResponse.errorData)
        : new UnknownBeaconError();
    const actions = [
        {
            text: error.title,
            isBold: true
        },
        {
            text: error.description
        }
    ];
    if (response.errorResponse.errorType === BeaconErrorType.TRANSACTION_INVALID_ERROR &&
        response.errorResponse.errorData) {
        actions.push({
            text: '',
            actionText: 'Show Details',
            actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
                yield closeToast();
                yield openAlert({
                    title: error.title,
                    // eslint-disable-next-line @typescript-eslint/unbound-method
                    body: error.fullDescription.description,
                    data: error.fullDescription.data,
                    buttons
                });
            })
        });
    }
    yield openToast({
        body: `{{wallet}}\u00A0 has returned an error`,
        timer: response.errorResponse.errorType === BeaconErrorType.ABORTED_ERROR
            ? SUCCESS_TIMER
            : undefined,
        state: 'finished',
        walletInfo: response.walletInfo,
        actions
    });
});
/**
 * Show a rate limit reached toast
 */
const showRateLimitReached = () => __awaiter(void 0, void 0, void 0, function* () {
    openAlert({
        title: 'Error',
        body: 'Rate limit reached. Please slow down',
        buttons: [{ text: 'Done', style: 'outline' }],
        timer: 3000
    }).catch((toastError) => console.error(toastError));
});
/**
 * Show a "connection successful" alert for 1.5 seconds
 */
const showExtensionConnectedAlert = () => __awaiter(void 0, void 0, void 0, function* () {
    yield closeAlerts();
});
/**
 * Show a "channel closed" alert for 1.5 seconds
 */
const showChannelClosedAlert = () => __awaiter(void 0, void 0, void 0, function* () {
    yield openAlert({
        title: 'Channel closed',
        body: `Your peer has closed the connection.`,
        buttons: [{ text: 'Done', style: 'outline' }],
        timer: 1500
    });
});
const showInternalErrorAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const buttons = [...((_a = data.buttons) !== null && _a !== void 0 ? _a : [])];
    buttons.push({ text: 'Done', style: 'outline' });
    const alertConfig = {
        title: 'Internal Error',
        body: data.text,
        buttons
    };
    yield openAlert(alertConfig);
});
/**
 * Show a connect alert with QR code
 *
 * @param data The data that is emitted by the PAIR_INIT event
 */
const showPairAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const alertConfig = {
        title: 'Choose your preferred wallet',
        body: `<p></p>`,
        pairingPayload: {
            p2pSyncCode: data.p2pPeerInfo,
            postmessageSyncCode: data.postmessagePeerInfo,
            preferredNetwork: data.preferredNetwork
        },
        // eslint-disable-next-line @typescript-eslint/unbound-method
        closeButtonCallback: data.abortedHandler,
        disclaimerText: data.disclaimerText
    };
    yield openAlert(alertConfig);
});
/**
 * Show a "Permission Granted" alert
 *
 * @param data The data that is emitted by the PERMISSION_REQUEST_SUCCESS event
 */
const showPermissionSuccessAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const { output } = data;
    yield openToast({
        body: `{{wallet}}\u00A0 has granted permission`,
        timer: SUCCESS_TIMER,
        walletInfo: data.walletInfo,
        state: 'finished',
        actions: [
            {
                text: 'Address',
                actionText: shortenString(output.address),
                isBold: true
            },
            {
                text: 'Network',
                actionText: `${output.network.type}`
            },
            {
                text: 'Permissions',
                actionText: output.scopes.join(', ')
            }
        ]
    });
});
/**
 * Show an "Operation Broadcasted" alert
 *
 * @param data The data that is emitted by the OPERATION_REQUEST_SUCCESS event
 */
const showOperationSuccessAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const { account, output, blockExplorer } = data;
    yield openToast({
        body: `{{wallet}}\u00A0 successfully submitted operation`,
        timer: SUCCESS_TIMER,
        state: 'finished',
        walletInfo: data.walletInfo,
        actions: [
            {
                text: shortenString(output.transactionHash),
                isBold: true,
                actionText: `Open Blockexplorer`,
                actionLogo: 'external',
                actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
                    const link = yield blockExplorer.getTransactionLink(output.transactionHash, account.network);
                    window.open(link, '_blank');
                    yield closeToast();
                })
            }
        ]
    });
});
/**
 * Show a "Transaction Signed" alert
 *
 * @param data The data that is emitted by the SIGN_REQUEST_SUCCESS event
 */
const showSignSuccessAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const output = data.output;
    yield openToast({
        body: `{{wallet}}\u00A0 successfully signed payload`,
        timer: SUCCESS_TIMER,
        state: 'finished',
        walletInfo: data.walletInfo,
        actions: [
            {
                text: `Signature: ${shortenString(output.signature)}`,
                actionText: 'Copy to clipboard',
                actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
                    navigator.clipboard.writeText(output.signature).then(() => {
                        logger.log('showSignSuccessAlert', 'Copying to clipboard was successful!');
                    }, (err) => {
                        logger.error('showSignSuccessAlert', 'Could not copy text to clipboard: ', err);
                    });
                    yield closeToast();
                })
            }
        ]
    });
});
/**
 * Show a "Transaction Signed" alert
 *
 * @param data The data that is emitted by the ENCRYPT_REQUEST_SUCCESS event
 */
// TODO: ENCRYPTION
// const showEncryptSuccessAlert = async (
//   data: BeaconEventType[BeaconEvent.ENCRYPT_REQUEST_SUCCESS]
// ): Promise<void> => {
//   const output = data.output
//   await openToast({
//     body: `{{wallet}}\u00A0 successfully ${
//       data.output.cryptoOperation === EncryptionOperation.ENCRYPT ? 'encrypted' : 'decrypted'
//     } payload`,
//     timer: SUCCESS_TIMER,
//     state: 'finished',
//     walletInfo: data.walletInfo,
//     actions: [
//       {
//         text: `Payload: <strong>${shortenString(output.payload)}</strong>`,
//         actionText: 'Copy to clipboard',
//         actionCallback: async (): Promise<void> => {
//           navigator.clipboard.writeText(output.payload).then(
//             () => {
//               logger.log('showSignSuccessAlert', 'Copying to clipboard was successful!')
//             },
//             (err) => {
//               logger.error('showSignSuccessAlert', 'Could not copy text to clipboard: ', err)
//             }
//           )
//           await closeToast()
//         }
//       }
//     ]
//   })
// }
/**
 * Show a "Broadcasted" alert
 *
 * @param data The data that is emitted by the BROADCAST_REQUEST_SUCCESS event
 */
const showBroadcastSuccessAlert = (data) => __awaiter(void 0, void 0, void 0, function* () {
    const { network, output, blockExplorer } = data;
    yield openToast({
        body: `{{wallet}}\u00A0 successfully injected operation`,
        timer: SUCCESS_TIMER,
        state: 'finished',
        walletInfo: data.walletInfo,
        actions: [
            {
                text: shortenString(output.transactionHash),
                isBold: true,
                actionText: `Open Blockexplorer`,
                actionLogo: 'external',
                actionCallback: () => __awaiter(void 0, void 0, void 0, function* () {
                    const link = yield blockExplorer.getTransactionLink(output.transactionHash, network);
                    window.open(link, '_blank');
                    yield closeToast();
                })
            }
        ]
    });
});
const emptyHandler = () => () => __awaiter(void 0, void 0, void 0, function* () {
    //
});
/**
 * The default event handlers
 */
export const defaultEventCallbacks = {
    [BeaconEvent.PERMISSION_REQUEST_SENT]: showSentToast,
    [BeaconEvent.PERMISSION_REQUEST_SUCCESS]: showPermissionSuccessAlert,
    [BeaconEvent.PERMISSION_REQUEST_ERROR]: showErrorToast,
    [BeaconEvent.OPERATION_REQUEST_SENT]: showSentToast,
    [BeaconEvent.OPERATION_REQUEST_SUCCESS]: showOperationSuccessAlert,
    [BeaconEvent.OPERATION_REQUEST_ERROR]: showErrorToast,
    [BeaconEvent.SIGN_REQUEST_SENT]: showSentToast,
    [BeaconEvent.SIGN_REQUEST_SUCCESS]: showSignSuccessAlert,
    [BeaconEvent.SIGN_REQUEST_ERROR]: showErrorToast,
    // TODO: ENCRYPTION
    // [BeaconEvent.ENCRYPT_REQUEST_SENT]: showSentToast,
    // [BeaconEvent.ENCRYPT_REQUEST_SUCCESS]: showEncryptSuccessAlert,
    // [BeaconEvent.ENCRYPT_REQUEST_ERROR]: showErrorToast,
    [BeaconEvent.BROADCAST_REQUEST_SENT]: showSentToast,
    [BeaconEvent.BROADCAST_REQUEST_SUCCESS]: showBroadcastSuccessAlert,
    [BeaconEvent.BROADCAST_REQUEST_ERROR]: showErrorToast,
    [BeaconEvent.ACKNOWLEDGE_RECEIVED]: showAcknowledgedToast,
    [BeaconEvent.LOCAL_RATE_LIMIT_REACHED]: showRateLimitReached,
    [BeaconEvent.NO_PERMISSIONS]: showNoPermissionAlert,
    [BeaconEvent.ACTIVE_ACCOUNT_SET]: emptyHandler(),
    [BeaconEvent.ACTIVE_TRANSPORT_SET]: emptyHandler(),
    [BeaconEvent.SHOW_PREPARE]: showPrepare,
    [BeaconEvent.HIDE_UI]: hideUI,
    [BeaconEvent.PAIR_INIT]: showPairAlert,
    [BeaconEvent.PAIR_SUCCESS]: showExtensionConnectedAlert,
    [BeaconEvent.CHANNEL_CLOSED]: showChannelClosedAlert,
    [BeaconEvent.INTERNAL_ERROR]: showInternalErrorAlert,
    [BeaconEvent.UNKNOWN]: emptyHandler()
};
/**
 * @internalapi
 *
 * Handles beacon events
 */
export class BeaconEventHandler {
    constructor(eventsToOverride = {}, overrideAll) {
        this.callbackMap = {
            [BeaconEvent.PERMISSION_REQUEST_SENT]: [defaultEventCallbacks.PERMISSION_REQUEST_SENT],
            [BeaconEvent.PERMISSION_REQUEST_SUCCESS]: [defaultEventCallbacks.PERMISSION_REQUEST_SUCCESS],
            [BeaconEvent.PERMISSION_REQUEST_ERROR]: [defaultEventCallbacks.PERMISSION_REQUEST_ERROR],
            [BeaconEvent.OPERATION_REQUEST_SENT]: [defaultEventCallbacks.OPERATION_REQUEST_SENT],
            [BeaconEvent.OPERATION_REQUEST_SUCCESS]: [defaultEventCallbacks.OPERATION_REQUEST_SUCCESS],
            [BeaconEvent.OPERATION_REQUEST_ERROR]: [defaultEventCallbacks.OPERATION_REQUEST_ERROR],
            [BeaconEvent.SIGN_REQUEST_SENT]: [defaultEventCallbacks.SIGN_REQUEST_SENT],
            [BeaconEvent.SIGN_REQUEST_SUCCESS]: [defaultEventCallbacks.SIGN_REQUEST_SUCCESS],
            [BeaconEvent.SIGN_REQUEST_ERROR]: [defaultEventCallbacks.SIGN_REQUEST_ERROR],
            // TODO: ENCRYPTION
            // [BeaconEvent.ENCRYPT_REQUEST_SENT]: [defaultEventCallbacks.ENCRYPT_REQUEST_SENT],
            // [BeaconEvent.ENCRYPT_REQUEST_SUCCESS]: [defaultEventCallbacks.ENCRYPT_REQUEST_SUCCESS],
            // [BeaconEvent.ENCRYPT_REQUEST_ERROR]: [defaultEventCallbacks.ENCRYPT_REQUEST_ERROR],
            [BeaconEvent.BROADCAST_REQUEST_SENT]: [defaultEventCallbacks.BROADCAST_REQUEST_SENT],
            [BeaconEvent.BROADCAST_REQUEST_SUCCESS]: [defaultEventCallbacks.BROADCAST_REQUEST_SUCCESS],
            [BeaconEvent.BROADCAST_REQUEST_ERROR]: [defaultEventCallbacks.BROADCAST_REQUEST_ERROR],
            [BeaconEvent.ACKNOWLEDGE_RECEIVED]: [defaultEventCallbacks.ACKNOWLEDGE_RECEIVED],
            [BeaconEvent.LOCAL_RATE_LIMIT_REACHED]: [defaultEventCallbacks.LOCAL_RATE_LIMIT_REACHED],
            [BeaconEvent.NO_PERMISSIONS]: [defaultEventCallbacks.NO_PERMISSIONS],
            [BeaconEvent.ACTIVE_ACCOUNT_SET]: [defaultEventCallbacks.ACTIVE_ACCOUNT_SET],
            [BeaconEvent.ACTIVE_TRANSPORT_SET]: [defaultEventCallbacks.ACTIVE_TRANSPORT_SET],
            [BeaconEvent.SHOW_PREPARE]: [defaultEventCallbacks.SHOW_PREPARE],
            [BeaconEvent.HIDE_UI]: [defaultEventCallbacks.HIDE_UI],
            [BeaconEvent.PAIR_INIT]: [defaultEventCallbacks.PAIR_INIT],
            [BeaconEvent.PAIR_SUCCESS]: [defaultEventCallbacks.PAIR_SUCCESS],
            [BeaconEvent.CHANNEL_CLOSED]: [defaultEventCallbacks.CHANNEL_CLOSED],
            [BeaconEvent.INTERNAL_ERROR]: [defaultEventCallbacks.INTERNAL_ERROR],
            [BeaconEvent.UNKNOWN]: [defaultEventCallbacks.UNKNOWN]
        };
        if (overrideAll) {
            this.setAllHandlers();
        }
        this.overrideDefaults(eventsToOverride);
    }
    /**
     * A method to subscribe to a specific beacon event and register a callback
     *
     * @param event The event being emitted
     * @param eventCallback The callback that will be invoked
     */
    on(event, eventCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            const listeners = this.callbackMap[event] || [];
            listeners.push(eventCallback);
            this.callbackMap[event] = listeners;
        });
    }
    /**
     * Emit a beacon event
     *
     * @param event The event being emitted
     * @param data The data to be emit
     */
    emit(event, data, eventCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            const listeners = this.callbackMap[event];
            if (listeners && listeners.length > 0) {
                listeners.forEach((listener) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        yield listener(data, eventCallback);
                    }
                    catch (listenerError) {
                        logger.error(`error handling event ${event}`, listenerError);
                    }
                }));
            }
        });
    }
    /**
     * Override beacon event default callbacks. This can be used to disable default alert/toast behaviour
     *
     * @param eventsToOverride An object with the events to override
     */
    overrideDefaults(eventsToOverride) {
        Object.keys(eventsToOverride).forEach((untypedEvent) => {
            const eventType = untypedEvent;
            const event = eventsToOverride[eventType];
            if (event) {
                this.callbackMap[eventType] = [event.handler];
            }
        });
    }
    /**
     * Set all event callbacks to a specific handler.
     */
    setAllHandlers(handler) {
        Object.keys(this.callbackMap).forEach((untypedEvent) => {
            const eventType = untypedEvent;
            this.callbackMap[eventType] = [];
            if (handler) {
                this.callbackMap[eventType].push(handler);
            }
            else {
                this.callbackMap[eventType].push((...data) => {
                    logger.log(untypedEvent, ...data);
                });
            }
        });
    }
}
//# sourceMappingURL=events.js.map